import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, Chip, Typography } from "@mui/material";
import EMPAccordion from "../../../shared/components/EMPAccordion/EMPAccordion";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";
import MotionChevronRightCircle from "../../../shared/Icons/MotionChevronRightCircle";
import MotionInfo from "../../../shared/Icons/MotionInfo";
import useRoleUsers from "../../../shared/hooks/useRoleUsers";
import OnboardingPageTitle from "../../../shared/components/OnboardingPageTitle/OnboardingPageTitle";
import accordionStyles from "../../../shared/components/EMPAccordion/EMPAccordionLincoln.module.scss";

const BODY_1_REGULAR_FONT =
  'var(--body-1-regular-font-family, "Lincoln Proxima Nova")';
const BODY_1_BOLD_FONT =
  'var(--body-1-bold-font-family, "Lincoln Proxima Nova")';
const BODY_2_REGULAR_FONT =
  'var(--body-2-regular-font-family, "Lincoln Proxima Nova")';

const toMembers = (users = []) =>
  users.map((u, i) => ({
    id: i + 1,
    name: u.userId,
    role: u.role ?? "Dealer/Partner",
    phone: u.phone ?? "123-456-7890",
  }));

const memberPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  role: PropTypes.string,
  phone: PropTypes.string,
});

const MemberCard = ({ name, role, phone }) => (
  <Box
    sx={{
      border: "1px solid #E5E5E5",
      borderRadius: "8px",
      padding: "16px",
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    }}
  >
    <Typography
      variant="body1"
      sx={{ fontWeight: 600, fontFamily: BODY_1_BOLD_FONT }}
    >
      {name}
    </Typography>
    {(role || phone) && (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontFamily: BODY_2_REGULAR_FONT }}
      >
        {[role, phone].filter(Boolean).join(", ")}
      </Typography>
    )}
  </Box>
);

MemberCard.propTypes = {
  name: PropTypes.string.isRequired,
  role: PropTypes.string,
  phone: PropTypes.string,
};

MemberCard.defaultProps = {
  role: "",
  phone: "",
};

const StatusChip = ({ completed }) => {
  const { t } = useTranslation();

  return (
    <Chip
      label={
        completed
          ? t("DealerOnboarding.content.entitlements.shared.statusCompleted")
          : t("DealerOnboarding.content.entitlements.shared.statusNotCompleted")
      }
      size="small"
      sx={{
        backgroundColor: completed ? "#E5F3E6" : "#FCF2E6",
        color: completed ? "#006800" : "#9F5801",
        fontFamily: BODY_2_REGULAR_FONT,
        fontWeight: 400,
        fontSize: "0.75rem",
        borderRadius: "4px",
      }}
    />
  );
};

StatusChip.propTypes = {
  completed: PropTypes.bool.isRequired,
};

const InfoMessage = ({ message }) => (
  <Box
    sx={{ display: "flex", alignItems: "flex-start", gap: "8px", mt: "16px" }}
  >
    <MotionInfo
      sx={{
        fontSize: "20px",
        mt: "2px",
        flexShrink: 0,
        "& path:nth-of-type(1)": { fill: "#485b65" },
        "& path:nth-of-type(2)": { fill: "#485b65" },
        "& path:nth-of-type(3)": { fill: "#ffffff" },
        "& path:nth-of-type(4)": { fill: "#ffffff" },
      }}
    />
    <Typography
      variant="body2"
      sx={{ color: "text.secondary", fontFamily: BODY_2_REGULAR_FONT }}
    >
      {message}
    </Typography>
  </Box>
);

InfoMessage.propTypes = {
  message: PropTypes.string,
};

InfoMessage.defaultProps = {
  message: "",
};

const SetEntitlementsButton = () => {
  const { t } = useTranslation();

  return (
    <Box sx={{ mt: "32px" }}>
      <EMPButton
        variant="contained"
        href="https://www.accessmgmt.ford.com/aga-home"
        target="_blank"
        rel="noopener noreferrer"
        endIcon={<MotionChevronRightCircle />}
        testId="set-entitlements-button"
        sx={{
          minWidth: "169px",
          height: "40px",
          borderRadius: "4px",
          padding: "8px 16px",
          fontFamily: BODY_2_REGULAR_FONT,
          fontWeight: 400,
          fontSize: "14px",
          backgroundColor: "#485b65 !important",
          color: "#ffffff !important",
          "&:hover": { backgroundColor: "#3a4a52 !important" },
          "&:disabled": {
            backgroundColor: "#e5e5e5 !important",
            color: "rgba(0, 0, 0, 0.38) !important",
          },
        }}
      >
        {t("DealerOnboarding.content.entitlements.shared.setEntitlementButton")}
      </EMPButton>
    </Box>
  );
};

const MemberGrid = ({ members }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: 2,
      mt: "16px",
    }}
  >
    {members.map((member) => (
      <MemberCard key={member.id} {...member} />
    ))}
  </Box>
);

MemberGrid.propTypes = {
  members: PropTypes.arrayOf(memberPropType).isRequired,
};

const EntitlementSection = ({
  heading,
  completed,
  expanded,
  onChange,
  entitlementLabelBold,
  entitlementLabelValue,
  infoMessage,
  listLabel,
  members,
  showListAlways = false,
  children,
}) => (
  <EMPAccordion
    heading={heading}
    headingClassName={accordionStyles.accordionHeading}
    expanded={expanded}
    onChange={onChange}
    prefixIconType={completed ? "check" : "caution"}
    toggleIconColor="grey"
  >
    {children}

    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: "32px" }}>
      <Typography variant="body1" sx={{ fontFamily: BODY_1_REGULAR_FONT }}>
        {entitlementLabelBold} {entitlementLabelValue}
      </Typography>
      <StatusChip completed={completed} />
    </Box>

    {!completed && !showListAlways ? (
      <InfoMessage message={infoMessage} />
    ) : (
      <>
        <Typography
          variant="body1"
          sx={{ fontWeight: 600, mt: "32px", fontFamily: BODY_1_BOLD_FONT }}
        >
          {listLabel}
        </Typography>
        <MemberGrid members={members} />
      </>
    )}

    <SetEntitlementsButton />
  </EMPAccordion>
);

EntitlementSection.propTypes = {
  heading: PropTypes.string.isRequired,
  completed: PropTypes.bool.isRequired,
  expanded: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  entitlementLabelBold: PropTypes.string.isRequired,
  entitlementLabelValue: PropTypes.string.isRequired,
  infoMessage: PropTypes.string,
  listLabel: PropTypes.string.isRequired,
  members: PropTypes.arrayOf(memberPropType).isRequired,
  showListAlways: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

EntitlementSection.defaultProps = {
  infoMessage: "",
  showListAlways: false,
};

const SECTION_DEFS = [
  {
    id: "kdm",
    i18nSuffix: "kdm",
    roleKey: "EMP_Onboard",
    showListAlways: true,
    descriptionType: "two-paragraph",
  },
  {
    id: "vehiclePricing",
    i18nSuffix: "vehiclePricing",
    roleKey: "VLAPP",
    descriptionType: "bold-paragraph",
  },
  {
    id: "accessories",
    i18nSuffix: "accessories",
    roleKey: "EMP_eComm_Accessories",
    descriptionType: "bold-paragraph",
  },
  {
    id: "customerHandling",
    i18nSuffix: "customerHandling",
    roleKey: "EMP_ECOMM_CUSTOMERHANDLING",
    descriptionType: "two-paragraph",
  },
];

const renderSectionDescription = (def, t) => {
  const base = `DealerOnboarding.content.entitlements.${def.i18nSuffix}`;
  if (def.descriptionType === "two-paragraph") {
    return (
      <>
        <Typography variant="body1" sx={{ fontFamily: BODY_1_REGULAR_FONT }}>
          {t(`${base}.description1`)}
        </Typography>
        <Typography
          variant="body1"
          sx={{ mt: "32px", fontFamily: BODY_1_REGULAR_FONT }}
        >
          {t(`${base}.description2`)}
        </Typography>
      </>
    );
  }
  return (
    <Typography variant="body1" sx={{ fontFamily: BODY_1_REGULAR_FONT }}>
      {t(`${base}.descriptionPre`)}{" "}
      <strong>{t(`${base}.descriptionBold`)}</strong>
      {t(`${base}.descriptionPost`)}
    </Typography>
  );
};

const OnboardingEntitlements = ({
  user,
  kdmCompleted,
  vehiclePricingCompleted,
  accessoriesCompleted,
  customerHandlingCompleted,
}) => {
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState({
    kdm: false,
    vehiclePricing: false,
    accessories: false,
    customerHandling: false,
  });

  const { data: roleUsers = {} } = useRoleUsers(
    user?.commonId || user?.commonDealerId
  );

  const handleAccordionChange = (key) => () =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const completedBySection = {
    kdm: kdmCompleted,
    vehiclePricing: vehiclePricingCompleted,
    accessories: accessoriesCompleted,
    customerHandling: customerHandlingCompleted,
  };

  return (
    <div className="tw-bg-white tw-rounded-lg tw-p-8">
      <OnboardingPageTitle testId="entitlements-title" className="tw-mb-6">
        {t("DealerOnboarding.content.onboardingSteps.entitlements.title")}
      </OnboardingPageTitle>

      <div data-testid="onboarding-entitlements">
        {SECTION_DEFS.map((def) => {
          const base = `DealerOnboarding.content.entitlements.${def.i18nSuffix}`;
          return (
            <EntitlementSection
              key={def.id}
              heading={t(`${base}.heading`)}
              completed={completedBySection[def.id]}
              expanded={expanded[def.id]}
              onChange={handleAccordionChange(def.id)}
              entitlementLabelBold={t(`${base}.entitlementLabelBold`)}
              entitlementLabelValue={t(`${base}.entitlementLabelValue`)}
              infoMessage={
                def.showListAlways ? undefined : t(`${base}.infoMessage`)
              }
              listLabel={t(`${base}.listLabel`)}
              members={toMembers(roleUsers[def.roleKey])}
              showListAlways={def.showListAlways}
            >
              {renderSectionDescription(def, t)}
            </EntitlementSection>
          );
        })}
      </div>
    </div>
  );
};

OnboardingEntitlements.propTypes = {
  user: PropTypes.shape({
    commonId: PropTypes.string,
    commonDealerId: PropTypes.string,
  }),
  kdmCompleted: PropTypes.bool,
  vehiclePricingCompleted: PropTypes.bool,
  accessoriesCompleted: PropTypes.bool,
  customerHandlingCompleted: PropTypes.bool,
};

OnboardingEntitlements.defaultProps = {
  user: null,
  kdmCompleted: false,
  vehiclePricingCompleted: false,
  accessoriesCompleted: false,
  customerHandlingCompleted: false,
};

export default OnboardingEntitlements;
